import { computed, type ComputedRef, type Ref } from 'vue'
import { useRoute, type RouteLocationNamedRaw } from 'vue-router'

import { RouteName } from '@/router/RouteName'
import type { OrganizationId } from '@/types/OrganizationId'

export interface BackNavigation {
  destination: RouteLocationNamedRaw
  label: string
}

const mappingRouteNames: string[] = [RouteName.theMappingView, RouteName.theOutdoorMappingView]

/**
 * Resolves where the header back link should point for the current route, or
 * undefined when the current route has nowhere to go back to.
 */
export function useBackNavigation(isSuperAdmin: Ref<boolean>): {
  backNavigation: ComputedRef<BackNavigation | undefined>
} {
  const route = useRoute()

  const backNavigation = computed<BackNavigation | undefined>(() => {
    if (isMappingRoute()) return backToOrganization()
    if (isOrganizationRouteForSuperAdmin()) return backToOrganizationPicker()

    return undefined
  })

  function isMappingRoute(): boolean {
    return mappingRouteNames.includes(String(route.name))
  }

  function backToOrganization(): BackNavigation {
    return {
      destination: {
        name: RouteName.organizationView,
        params: { organizationId: route.params.organizationId as OrganizationId },
      },
      label: 'Back to organization',
    }
  }

  function isOrganizationRouteForSuperAdmin(): boolean {
    return route.name === RouteName.organizationView && isSuperAdmin.value
  }

  function backToOrganizationPicker(): BackNavigation {
    return {
      destination: { name: RouteName.rootView },
      label: 'Back to organizations',
    }
  }

  return { backNavigation }
}
